import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  Skeleton,
  ToggleGroup,
} from '@digdir/designsystemet-react';
import { PlusIcon } from '@navikt/aksel-icons';
import { useGetCustomSurveyTemplatesQuery } from '../../hooks/queries/useGetCustomSurveyTemplatesQuery';
import { Routes } from '../../enums/Routes';
import { TemplateCard } from './components/TemplateCard';
import { ScopeFilter, SortOption, StatusFilter, templateStats, visibleTemplates } from './templatesView';
import classes from './CustomSurveyTemplatesPage.module.css';

const STATUSES: StatusFilter[] = ['all', 'active', 'archived'];

export const CustomSurveyTemplatesPage = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [sort, setSort] = useState<SortOption>('lastUsed');

  const { data: templates = [], isLoading, error } = useGetCustomSurveyTemplatesQuery();
  const stats = templateStats(templates);
  const visible = visibleTemplates(templates, { query, status, scope, sort });
  const counts: Record<StatusFilter, number> = { all: stats.total, active: stats.active, archived: stats.archived };

  const showAll = () => {
    setQuery('');
    setStatus('all');
    setScope('all');
  };

  const content = () => {
    if (isLoading) {
      return (
        <div className={classes.loading}>
          <span className="ds-sr-only">{t('customSurveys.templates.loading')}</span>
          <Skeleton variant="rectangle" height="96px" />
          <Skeleton variant="rectangle" height="320px" />
        </div>
      );
    }

    if (error) {
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="xs">
            {t('customSurveys.templates.error.title')}
          </Heading>
          <Paragraph>{t('customSurveys.templates.error.message')}</Paragraph>
        </Alert>
      );
    }

    if (templates.length === 0) {
      return (
        <div className={classes.box}>
          <Heading level={2} data-size="xs">
            {t('customSurveys.templates.emptyState.title')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('customSurveys.templates.emptyState.description')}
          </Paragraph>
        </div>
      );
    }

    return (
      <>
        <dl className={classes.stats}>
          <div>
            <dt>{t('customSurveys.templates.stats.total')}</dt>
            <dd>{stats.total}</dd>
          </div>
          <div>
            <dt>{t('customSurveys.templates.stats.active')}</dt>
            <dd>{stats.active}</dd>
          </div>
          <div>
            <dt>{t('customSurveys.templates.stats.archived')}</dt>
            <dd>{stats.archived}</dd>
          </div>
          <div>
            <dt>{t('customSurveys.templates.stats.recentlyUsed')}</dt>
            <dd>{stats.recentlyUsed}</dd>
          </div>
        </dl>

        <div role="search" className={classes.filters}>
          <Field className={classes.searchField}>
            <Label htmlFor="templates-search">{t('customSurveys.templates.filters.search')}</Label>
            <Input
              id="templates-search"
              type="search"
              value={query}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
          </Field>
          <div className={classes.filterGroup}>
            <ToggleGroup
              value={status}
              onChange={(value) => setStatus(value as StatusFilter)}
              aria-label={t('customSurveys.templates.filters.status')}
              className={classes.statusToggle}
            >
              {STATUSES.map((value) => (
                <ToggleGroup.Item key={value} value={value}>
                  {t(`customSurveys.templates.filters.${value}`)}
                  <span className={classes.count}>{counts[value]}</span>
                </ToggleGroup.Item>
              ))}
            </ToggleGroup>
            <Field className={classes.selectField}>
              <Label htmlFor="templates-scope" data-size="sm">
                {t('customSurveys.templates.filters.scope')}
              </Label>
              <Select id="templates-scope" value={scope} onChange={(event) => setScope(event.target.value as ScopeFilter)}>
                <Select.Option value="all">{t('customSurveys.templates.filters.scopeAll')}</Select.Option>
                <Select.Option value="company">{t('customSurveys.templates.filters.scopeCompany')}</Select.Option>
                <Select.Option value="team">{t('customSurveys.templates.filters.scopeTeam')}</Select.Option>
              </Select>
            </Field>
            <Field className={classes.selectField}>
              <Label htmlFor="templates-sort" data-size="sm">
                {t('customSurveys.templates.filters.sort')}
              </Label>
              <Select id="templates-sort" value={sort} onChange={(event) => setSort(event.target.value as SortOption)}>
                <Select.Option value="lastUsed">{t('customSurveys.templates.filters.sortLastUsed')}</Select.Option>
                <Select.Option value="mostUsed">{t('customSurveys.templates.filters.sortMostUsed')}</Select.Option>
                <Select.Option value="name">{t('customSurveys.templates.filters.sortName')}</Select.Option>
              </Select>
            </Field>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className={`${classes.box} ${classes.noMatch}`}>
            <Heading level={2} data-size="xs">
              {t('customSurveys.templates.noMatch.title')}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('customSurveys.templates.noMatch.description')}
            </Paragraph>
            <Button variant="secondary" data-size="sm" onClick={showAll}>
              {t('customSurveys.templates.noMatch.showAll')}
            </Button>
          </div>
        ) : (
          <ul className={classes.grid}>
            {visible.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </ul>
        )}
      </>
    );
  };

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <div className={classes.intro}>
          <span className={classes.eyebrow}>{t('customSurveys.templates.eyebrow')}</span>
          <Heading level={1} data-size="xl">
            {t('customSurveys.templates.pageTitle')}
          </Heading>
          <Paragraph data-size="md">{t('customSurveys.templates.pageDescription')}</Paragraph>
        </div>
        <Button asChild>
          <Link to={Routes.CreateCustomSurveyTemplate}>
            <PlusIcon aria-hidden />
            {t('customSurveys.templates.createButton')}
          </Link>
        </Button>
      </div>
      {content()}
    </div>
  );
};
